import { useContext } from 'react';
import AppContext from '../../../AppContext';
import DirectLossesRowData from './DirectLossesRowData';
import styles from './DirectLossesRow.module.css';
import { RatioFormat, DollarFormat } from '../../format';
import { DataResult } from '../../model/DataResult';
import RowHelmet from '../RowHelmet';
import LabelRow from '../LabelRow';

function rowClassSwitch(rowClass: string = '') {
  switch (rowClass) {
    case 'INDENT':
      return styles.indent;
    case 'TOTAL':
      return styles.total;
    default:
      return '';
  }
}

/**
 * Per-row visibility for the current risk-profile view, mirroring
 * RowBuilder.getDataDisplayForCurrentRepository. The subsection-level
 * reportViewList gate (in SubSection) decides whether the subsection renders
 * at all; this decides whether an individual row shows, greys, or hides
 * within a shown subsection. Full Profile (FR) always shows every row.
 */
function getDataDisplayClass(
  rowDisplay: DirectLossesRowData['rowDisplay'],
  riskRepository?: string
) {
  if (!riskRepository || riskRepository === 'FR' || !rowDisplay) {
    return '';
  }

  const display = rowDisplay[riskRepository.toLowerCase() as keyof typeof rowDisplay];

  if (display === 'HIDE') {
    return styles.hidden;
  } else if (display === 'GREY') {
    return styles.grey;
  }

  return '';
}

function hasAnyOutsideBenchmarks(
  percentOrPointChange: DataResult,
  dollarChange: DataResult,
  currentYearDLP: DataResult,
  currentYearPercentOfTotal: DataResult,
  priorYearDLP: DataResult,
  priorYearPercentOfTotal: DataResult,
  priorYearMinusOneDLP: DataResult,
  priorYearMinusOnePercentOfTotal: DataResult
) {
  return (
    percentOrPointChange?.outsideBenchmark ||
    dollarChange?.outsideBenchmark ||
    currentYearDLP?.outsideBenchmark ||
    currentYearPercentOfTotal?.outsideBenchmark ||
    priorYearDLP?.outsideBenchmark ||
    priorYearPercentOfTotal?.outsideBenchmark ||
    priorYearMinusOneDLP?.outsideBenchmark ||
    priorYearMinusOnePercentOfTotal?.outsideBenchmark
  );
}

export default function DirectLossesRow({
  rowKey,
  rowType,
  rowClass,
  label,
  benchmark,
  percentOrPointChange,
  dollarChange,
  currentYearDLP,
  currentYearPercentOfTotal,
  priorYearDLP,
  priorYearPercentOfTotal,
  priorYearMinusOneDLP,
  priorYearMinusOnePercentOfTotal,
  rowDisplay,
  sectionKey
}: DirectLossesRowData) {
  const { showBenchmark, riskRepository } = useContext(AppContext);

  if (label.trim().toLowerCase() === 'missing') {
    return null;
  }

  const displayClass = getDataDisplayClass(rowDisplay, riskRepository);

  if (rowType === 'LABEL') {
    return (
      <div
        id={
          'label-' +
          label
            .replace(/\s+/g, '-')
            .replace(/[^a-z^A-Z^0-9^-]+/g, '')
            .toLowerCase()
        }
        className={`${styles.container} ${showBenchmark ? '' : styles.collapse} ${displayClass}`.trim()}>
        <RowHelmet label={label} />
        <LabelRow></LabelRow>
      </div>
    );
  } else {
    return (
      <div
        id={sectionKey ? sectionKey + '-' + rowKey : 'rowKey-' + rowKey}
        className={`${styles.container} ${showBenchmark ? '' : styles.collapse} ${rowClassSwitch(
          rowClass
        )} ${displayClass}`.trim()}>
        <RowHelmet
          label={label}
          isOutsideBenchmark={hasAnyOutsideBenchmarks(
            percentOrPointChange,
            dollarChange,
            currentYearDLP,
            currentYearPercentOfTotal,
            priorYearDLP,
            priorYearPercentOfTotal,
            priorYearMinusOneDLP,
            priorYearMinusOnePercentOfTotal
          )}
          benchmarkText={benchmark}
        />
        <RatioFormat {...percentOrPointChange} showDefaultValue={true} />
        <DollarFormat {...dollarChange} showDefaultValue={true} />
        <p></p>
        <p></p>
        <DollarFormat {...currentYearDLP} />
        <RatioFormat {...currentYearPercentOfTotal} />
        <DollarFormat {...priorYearDLP} />
        <RatioFormat {...priorYearPercentOfTotal} />
        <DollarFormat {...priorYearMinusOneDLP} />
        <RatioFormat {...priorYearMinusOnePercentOfTotal} />
      </div>
    );
  }
}
